import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { SidebarProvider } from '@/components/ui/sidebar';
import { ATTR_AVIVADOR_PANEL } from '@/components/avivador/avivador-tipos';
import { Sidebar } from './sidebar';
import { GlobalSearch } from './global-search';
import { MobileGuard } from './mobile-guard';
import { FranjaPiloto } from './franja-piloto';
import { useGlobalSearchShortcut } from '@/hooks/use-global-search-shortcut';

const STORAGE_KEY = 'redeapp.sidebar.collapsed';
// Breakpoint de tablet (MVP B.1 punto 7): sin preferencia guardada todavía,
// la sidebar arranca colapsada por debajo de este ancho (matchMedia).
const TABLET_MAX_WIDTH = 1024;

function leerColapsadoInicial(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
  } catch {
    // localStorage bloqueado: seguimos al fallback de matchMedia
  }
  return window.matchMedia(`(max-width: ${TABLET_MAX_WIDTH}px)`).matches;
}

/** INPUT, TEXTAREA, SELECT o contenteditable (mismo criterio que
 *  avivador-teclado.ts:175-179, copiado — no se importa de ahí para no
 *  acoplar layout con el dominio del Avivador). */
function esCampoDeTexto(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el as HTMLElement).isContentEditable === true
  );
}

/** Hay un diálogo o alertdialog de Base UI abierto (modal «Nuevo cliente»,
 *  un AlertDialog de confirmación, estado-redes-dialog, etc.), no solo el
 *  panel del Avivador. `data-open` es el atributo real que pone el Popup de
 *  Base UI mientras está abierto (confirmado en ui/dialog.tsx y
 *  ui/alert-dialog.tsx: las mismas clases `data-open:animate-in` que
 *  maneja la animación de entrada/salida). */
function hayDialogoAbierto(): boolean {
  return document.querySelector('[role="dialog"][data-open], [role="alertdialog"][data-open]') !== null;
}

// Shell de la app: sidebar fija a la izquierda + un <main> scrolleable que
// persiste entre rutas (por eso el scroll-to-top manual al cambiar de
// pathname — React Router no lo resetea solo). Sin header de página (B.1
// punto 8): el buscador global vive en el sidebar y cada página trae su
// propio título en el contenido (patrón obras.tsx).
//
// R4b (Ajuste 31 de plan_migracion_reui.md): el SidebarProvider vive ACÁ,
// CONTROLADO sobre la misma clave de localStorage que ya usaba el sidebar
// propio — así el atajo ⌘B que trae ui/sidebar.tsx y el toggle manual
// comparten un solo estado. SIN SidebarInset: el <main> de siempre sigue
// siendo el que scrollea (de él dependen el velo y el panel del Avivador, la
// restauración del foco del Avivador, «Saltar al contenido» y el harness).
export function Layout() {
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const { open: searchOpen, setOpen: setSearchOpen } = useGlobalSearchShortcut();
  const [colapsado, setColapsado] = useState(leerColapsadoInicial);

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 });
  }, [pathname]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, colapsado ? '1' : '0');
    } catch {
      // localStorage bloqueado (modo privado estricto): se pierde solo la persistencia
    }
  }, [colapsado]);

  // ⌘B / Ctrl+B es el atajo que trae ui/sidebar.tsx (listener en `window`,
  // fase de burbuja). No se edita ese vendorizado (R0), así que acá se
  // intercepta en fase de CAPTURA — corre antes y, con `stopPropagation`,
  // el evento nunca llega al listener del vendorizado — cuando el panel del
  // Avivador está abierto, el foco está en un campo, o hay CUALQUIER
  // diálogo/alertdialog de Base UI abierto (Ajuste 31; generalizado: el
  // `onKeyDown` de DialogPopup solo frena las flechas/Home/End —
  // node_modules/@base-ui/react/dialog/popup/DialogPopup.js — así que sin
  // esto un ⌘B con el foco en un botón del diálogo, no un input, colapsaba
  // la sidebar detrás del modal).
  useEffect(() => {
    function interceptarAtajoSidebar(event: KeyboardEvent): void {
      if (event.key.toLowerCase() !== 'b' || !(event.metaKey || event.ctrlKey)) return;
      const panelAvivadorAbierto = document.querySelector(`[${ATTR_AVIVADOR_PANEL}]`) !== null;
      if (panelAvivadorAbierto || esCampoDeTexto(document.activeElement) || hayDialogoAbierto()) {
        event.stopPropagation();
      }
    }
    window.addEventListener('keydown', interceptarAtajoSidebar, { capture: true });
    return () => window.removeEventListener('keydown', interceptarAtajoSidebar, { capture: true });
  }, []);

  return (
    <SidebarProvider
      open={!colapsado}
      onOpenChange={open => setColapsado(!open)}
      style={{ '--sidebar-width': '232px', '--sidebar-width-icon': '62px' } as CSSProperties}
      className="h-full min-h-0 w-full overflow-hidden bg-surface-page"
    >
      {/* «Saltar al contenido»: aparece con el primer Tab y evita recorrer todo
          el menú (QA de Juan, 25/9). Lleva el foco al <main>. */}
      <a
        href="#contenido"
        onClick={e => {
          e.preventDefault();
          mainRef.current?.focus();
        }}
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:rounded-md focus:bg-surface-card focus:px-3 focus:py-2 focus:text-[13px] focus:font-medium focus:text-foreground focus:shadow-lg"
      >
        Saltar al contenido
      </a>
      <Sidebar onOpenSearch={() => setSearchOpen(true)} />
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Franja del piloto 4.0.0 (solo con MODO_PILOTO=1): arriba del
            contenido y fuera del <main>, así no se va con el scroll. */}
        <FranjaPiloto />
        <main ref={mainRef} id="contenido" tabIndex={-1} className="min-w-0 flex-1 overflow-y-auto focus:outline-none">
          <Outlet />
        </main>
      </div>
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
      <MobileGuard />
    </SidebarProvider>
  );
}
